import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FilterMetadata } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { PopoverModule } from 'primeng/popover';
import { Table, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { FilterCreateComponent } from 'src/app/components/misc/generic-table/filter/filter-create/filter-create.component';
import { FilterComponent } from 'src/app/components/misc/generic-table/filter/filter.component';
import { Country } from 'src/app/models/Country';
import { Address } from 'src/app/models/customer/Address';

import { Customer } from 'src/app/models/customer/Customer';
import { TableFilter } from 'src/app/models/TableFilter';
import { AuthService } from 'src/app/services/auth/auth.service';
import { SHARED_IMPORTS } from 'src/app/utils/other/standalone-import';
import { AppAction } from 'src/config/authorization.config';
import { environment } from 'src/environments/environment';
import { CustomerStatusBadgeComponent } from '../customer-status-badge/customer-status-badge.component';
import { TableActionComponent } from 'src/app/components/misc/table-action/table-action.component';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';

type VirtualTableCustomer = Customer & {
  virtual: {
    facilityAddress: string;
    customer: string;
    contactPerson: string;
    facilityAddressType: string;

    virtualSearch: {
      facilityAddressSearch: string;
    };
  };
};

@Component({
  selector: 'app-customer-table',
  templateUrl: './customer-table.component.html',
  styleUrls: ['./customer-table.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    SHARED_IMPORTS,
    TableModule,
    ButtonModule,
    FilterComponent,
    PopoverModule,
    TooltipModule,
    FilterCreateComponent,
    CustomerStatusBadgeComponent,
    TableActionComponent,
    IconField,
    InputIcon
  ]
})
export class CustomerTableComponent
  implements OnChanges, OnInit, OnDestroy, AfterViewInit
{
  private translate = inject(TranslateService);
  private authService = inject(AuthService);

  @ViewChild('tableElement') public tableElement?: Table;

  @ViewChild('appFilter') appFilter?: FilterComponent;

  @Input({ required: true }) customers: Customer[] = [];

  @Input({ required: true }) countries: Country[] = [];

  @Input() isLoading = false;

  @Input() showEditAction = true;

  @Input() showDeleteAction = true;

  @Input() showCorrespondenceAction = true;

  @Input() showCreateAction = true;

  @Input() showChooseAction = false;

  @Input() customerBadgeClickable = true;

  @Output() handleCreateCustomer = new EventEmitter<void>();

  @Output() handleUpdateCustomer = new EventEmitter<Customer>();

  @Output() handleDeleteCustomer = new EventEmitter<Customer>();

  @Output() handleViewCustomer = new EventEmitter<Customer>();

  @Output() handleNewCorrespondence = new EventEmitter<Customer>();

  @Output() handleCustomerChosen = new EventEmitter<Customer>();

  @Output() handleChangeIsActive = new EventEmitter<{
    value: boolean;
    customer: Customer;
  }>();

  @Output() handleChangeIsBlocked = new EventEmitter<{
    value: boolean;
    customer: Customer;
  }>();

  @Output() handleReloadCustomers = new EventEmitter<void>();

  tableConfig = environment.tableConfiguration;

  virtualCustomers: VirtualTableCustomer[] = [];

  displayFilterOverlay = false;

  isFilterApplied = false;

  activeFilter: number | null = null;

  filterSubscription: Subscription | null = null;

  ngOnInit(): void {
    this.updateVirtualCustomers();
  }

  ngAfterViewInit(): void {
    if (this.tableElement) {
      this.filterSubscription = this.tableElement.onFilter.subscribe(
        (event) => {
          if (!event.filters) {
            this.isFilterApplied = false;

            return;
          }

          this.isFilterApplied = Object.keys(event.filters).some((x) => {
            if (x === 'global') {
              return false;
            }

            const filter = this.tableElement?.filters[x] as FilterMetadata;

            return (
              filter.value !== null &&
              filter.value !== undefined &&
              filter.value !== ''
            );
          });
        }
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customers'] && changes['customers'].currentValue) {
      this.updateVirtualCustomers();
    }

    if (changes['countries'] && changes['countries'].currentValue) {
      this.updateVirtualCustomers();
    }
  }

  ngOnDestroy(): void {
    this.filterSubscription?.unsubscribe();
  }

  $can(action: AppAction): boolean {
    return this.authService.$can(action, 'Customer');
  }

  formatAddress(address: Address): string {
    const country = this.countries.find((c) => c.id === address.countryId);

    if (country) {
      const countryTranslation = this.translate.instant(
        `countries.${country.key}`
      );

      return `${address.street}, ${address.postalCode} ${address.city}, ${countryTranslation}`;
    }

    return `${address.street}, ${address.postalCode} ${address.city}`;
  }

  filterApplied(filter: TableFilter): void {
    this.activeFilter = filter.id;

    if (this.tableElement) {
      this.tableElement.filters = structuredClone(
        filter.filterData
      ) as Table['filters'];
      this.updateVirtualCustomers();
      this.tableElement._filter();
    }
  }

  clearFilters(): void {
    if (this.tableElement) {
      this.tableElement.clearFilterValues();
      this.tableElement.reset();
      localStorage.removeItem('customer-table-memory');
    }

    this.activeFilter = null;
  }

  saveFilterClicked(filterName: string): void {
    if (!this.tableElement) {
      throw new Error('Table element not found');
    }

    const tableFilter = new TableFilter();

    tableFilter.filterData = structuredClone(this.tableElement?.filters);
    tableFilter.table = 'customer';
    tableFilter.filterName = filterName;

    this.appFilter?.createFilter(tableFilter);
  }

  updateVirtualCustomers(): void {
    this.virtualCustomers = this.customers.map((customer) => {
      const facilityAddress = this.formatAddress(customer.facilityAddress);

      return {
        ...customer,
        virtual: {
          facilityAddress,
          customer: `${customer.name} ${customer.customerNumber}`,
          contactPerson: this.displayContactPerson(customer),
          facilityAddressType: this.translate.instant(
            `customerComponent.facilityAddressType.${customer.facilityAddressType}`
          ),
          virtualSearch: {
            facilityAddressSearch: `${this.translate.instant(
              `customerComponent.facilityAddressType.${customer.facilityAddressType}`
            )} ${facilityAddress}`
          }
        }
      } satisfies VirtualTableCustomer;
    });
  }

  displayContactPerson(customer: Customer): string {
    const mainContact = customer.contactPersons.find(
      (contactPerson) => contactPerson.isMainContact
    );

    if (!mainContact) {
      return '-';
    }

    if (mainContact.firstname) {
      return `${mainContact.firstname} ${mainContact.lastname}`;
    }

    return mainContact.lastname;
  }

  createCustomer() {
    this.handleCreateCustomer.emit();
  }

  updateCustomer(customer: Customer) {
    this.handleUpdateCustomer.emit(customer);
  }

  deleteCustomer(customer: Customer) {
    this.handleDeleteCustomer.emit(customer);
  }

  viewCustomer(customer: Customer) {
    this.handleViewCustomer.emit(customer);
  }

  newCorrespondence(customer: Customer) {
    this.handleNewCorrespondence.emit(customer);
  }

  changeIsActive(value: boolean, customer: Customer) {
    this.handleChangeIsActive.emit({ value, customer });
  }

  changeIsBlocked(value: boolean, customer: Customer) {
    this.handleChangeIsBlocked.emit({ value, customer });
  }

  chooseCustomer(customer: Customer) {
    this.handleCustomerChosen.emit(customer);
  }

  reloadCustomers() {
    this.handleReloadCustomers.emit();
  }
}
